import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  ClockIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  UserIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import ApprovalStatusBadge from './ApprovalStatusBadge';
import TimeEntryInlineEditableCell from './TimeEntryInlineEditableCell';
import { stripHtml } from '../utils/sanitize';

interface HierarchyNode {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  children: HierarchyNode[];
}

interface TimeEntry {
  id: string;
  number?: string;
  userId: string;
  customerId: string;
  engagementId: string;
  groupId: string;
  type: string;
  timeSpent: number;
  workedDate: string;
  description: string;
  task?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  remarks?: string;
  actionRequired?: string;
  approvedAt?: string;
  rejectedAt?: string;
  createdAt: string;
  Customer?: { id: string; accountName: string };
  Engagement?: { id: string; name: string; number: string };
  Group?: { id: string; name: string };
  User?: { id: string; firstName: string; lastName: string; email: string };
  ApprovedBy?: { id: string; firstName: string; lastName: string };
  RejectedBy?: { id: string; firstName: string; lastName: string };
}

interface HierarchicalTimeEntryTableProps {
  timeEntries: TimeEntry[];
  filterMonth: string[];
  filterCustomer: string;
  filterEngagement: string[];
  filterType: string[];
  filterStatus: string[];
  filterUser: string[];
  filterGroup: string[];
  visibleColumns: { key: string; label: string }[];
  typeLabels: Record<string, string>;
  isAdminOrManager: boolean;
  canEditTimeEntries: boolean;
  hasAdminRole: boolean;
  currentUserId?: string;
  onApproveClick: (entry: TimeEntry) => void;
  onRejectClick: (entry: TimeEntry) => void;
  onActivityClick: (entry: TimeEntry) => void;
  onBulkApprove: (entries: TimeEntry[]) => void;
  onBulkReject: (entries: TimeEntry[]) => void;
  /** Formatter for worked date (date-only, no timezone shift). When provided, used for Worked Date column. */
  formatWorkedDate?: (dateString: string) => string;
  /** Inline edit: persist a field update for an entry. */
  onFieldUpdate?: (entryId: string, payload: Record<string, unknown>) => Promise<void>;
  /** Entry ID currently being saved (show loader). */
  isSavingEntryId?: string | null;
  onValidationError?: (message: string) => void;
  todayDate?: string;
  /** Whether user can delete time entries (own only for manager; any for admin). */
  canDeleteTimeEntries?: boolean;
  /** Whether user can create (copy) time entries. */
  canCreateTimeEntries?: boolean;
  /** Called when user clicks Copy on an entry (opens modal in parent). */
  onCopyClick?: (entry: TimeEntry) => void;
  /** Called when user clicks Delete on an entry (opens confirmation in parent). */
  onDeleteClick?: (entry: TimeEntry) => void;
  /** Current list URL search string — passed to edit links so Back restores filters. */
  listSearch?: string;
}

// Helper functions
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatTime = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  }
  return `${mins}m`;
};

// Calculate approval status for a manager's team
type ApprovalStatus = 'all_approved' | 'in_progress' | 'none_approved' | 'no_entries';

function calculateTeamApprovalStatus(
  entries: TimeEntry[]
): ApprovalStatus {
  if (entries.length === 0) {
    return 'no_entries';
  }

  const approvedCount = entries.filter(e => e.status === 'APPROVED').length;
  const totalCount = entries.length;

  if (approvedCount === totalCount) {
    return 'all_approved';
  } else if (approvedCount > 0) {
    return 'in_progress';
  } else {
    return 'none_approved';
  }
}

// Get badge styling based on approval status
function getApprovalStatusStyle(status: ApprovalStatus): { bg: string; text: string; label: string } {
  switch (status) {
    case 'all_approved':
      return { bg: 'bg-green-100', text: 'text-green-700', label: 'All Approved' };
    case 'in_progress':
      return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'In Progress' };
    case 'none_approved':
      return { bg: 'bg-red-100', text: 'text-red-700', label: 'Pending' };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-500', label: 'No Entries' };
  }
}

// Flatten hierarchy for display, tracking which users are managers
interface FlatUser {
  user: HierarchyNode;
  level: number;
  isManager: boolean;
  managerId: string | null;
  managerName: string | null;
}

function flattenHierarchy(
  node: HierarchyNode,
  level: number = 0,
  managerId: string | null = null,
  managerName: string | null = null,
  timeEntriesByUser?: Map<string, TimeEntry[]>
): FlatUser[] {
  const result: FlatUser[] = [];
  const hasChildren = node.children && node.children.length > 0;

  // Add the current user
  result.push({
    user: node,
    level,
    isManager: hasChildren,
    managerId,
    managerName,
  });

  // Sort children: users with entries first, then users without entries
  const sortedChildren = [...(node.children || [])].sort((a, b) => {
    const aHasEntries = timeEntriesByUser ? (timeEntriesByUser.get(a.id)?.length || 0) > 0 : false;
    const bHasEntries = timeEntriesByUser ? (timeEntriesByUser.get(b.id)?.length || 0) > 0 : false;

    // Users with entries come first
    if (aHasEntries && !bHasEntries) return -1;
    if (!aHasEntries && bHasEntries) return 1;

    // If both have or don't have entries, sort alphabetically
    return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
  });

  // Add sorted children
  for (const child of sortedChildren) {
    result.push(...flattenHierarchy(child, level + 1, node.id, `${node.firstName} ${node.lastName}`, timeEntriesByUser));
  }

  return result;
}

// Collect all user IDs from hierarchy tree
function collectUserIds(node: HierarchyNode): string[] {
  const ids = [node.id];
  for (const child of node.children || []) {
    ids.push(...collectUserIds(child));
  }
  return ids;
}

// Find a node in the hierarchy tree by ID
function findNodeById(node: HierarchyNode, id: string): HierarchyNode | null {
  if (node.id === id) return node;
  for (const child of node.children || []) {
    const found = findNodeById(child, id);
    if (found) return found;
  }
  return null;
}

// Collect all descendant IDs (excluding the node itself)
function collectDescendantIds(node: HierarchyNode): string[] {
  const ids: string[] = [];
  for (const child of node.children || []) {
    ids.push(child.id);
    ids.push(...collectDescendantIds(child));
  }
  return ids;
}

export default function HierarchicalTimeEntryTable({
  timeEntries,
  filterUser,
  filterGroup,
  visibleColumns,
  typeLabels,
  isAdminOrManager,
  canEditTimeEntries,
  hasAdminRole,
  currentUserId,
  formatWorkedDate,
  onApproveClick,
  onRejectClick,
  onActivityClick,
  onBulkApprove,
  onBulkReject,
  onFieldUpdate,
  isSavingEntryId,
  onValidationError,
  todayDate = '',
  canDeleteTimeEntries,
  canCreateTimeEntries,
  onCopyClick,
  onDeleteClick,
  listSearch,
}: HierarchicalTimeEntryTableProps) {
  const editListState =
    listSearch !== undefined ? { timeEntriesListSearch: listSearch } : undefined;

  const canModifyEntry = useCallback(
    (entry: TimeEntry) =>
      canEditTimeEntries && (hasAdminRole || entry.userId === currentUserId),
    [canEditTimeEntries, hasAdminRole, currentUserId]
  );

  const canCopyEntry = useCallback(
    (entry: TimeEntry) =>
      Boolean(canCreateTimeEntries && onCopyClick) &&
      (hasAdminRole || entry.userId === currentUserId),
    [canCreateTimeEntries, hasAdminRole, currentUserId, onCopyClick]
  );

  const canDeleteEntry = useCallback(
    (entry: TimeEntry) =>
      Boolean(canDeleteTimeEntries && onDeleteClick) &&
      (hasAdminRole || entry.userId === currentUserId),
    [canDeleteTimeEntries, hasAdminRole, currentUserId, onDeleteClick]
  );
  // Two separate states: one for expanding team (reports), one for expanding entries
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());
  // Selection state for bulk actions
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<string>>(new Set());

  // Fetch hierarchy tree
  const { data: hierarchyData, isLoading: isLoadingHierarchy, error: hierarchyError } = useQuery({
    queryKey: ['myHierarchyTree'],
    queryFn: async () => {
      const response = await api.get('/users/my-hierarchy-tree');
      return response.data as HierarchyNode;
    },
  });

  // Auto-expand all teams when data loads
  React.useEffect(() => {
    if (hierarchyData) {
      const allIds = collectUserIds(hierarchyData);
      setExpandedTeams(new Set(allIds));
    }
  }, [hierarchyData]);

  // Group time entries by user ID
  // Backend already filters by hierarchy when myHierarchy=true, so no frontend filtering needed
  const timeEntriesByUser = useMemo(() => {
    const map = new Map<string, TimeEntry[]>();
    for (const entry of timeEntries) {
      const userId = entry.userId || entry.User?.id;
      if (!userId) continue;
      if (!map.has(userId)) {
        map.set(userId, []);
      }
      map.get(userId)!.push(entry);
    }
    // Sort entries by date within each user
    for (const entries of map.values()) {
      entries.sort((a, b) => new Date(b.workedDate).getTime() - new Date(a.workedDate).getTime());
    }
    return map;
  }, [timeEntries]);

  // Flatten hierarchy for rendering (sorted: users with entries first)
  // When filterUser is applied, only show those specific users
  // When filterGroup is applied, only show users who have entries (backend filters by group)
  const flatUsers = useMemo(() => {
    if (!hierarchyData) return [];
    const allFlatUsers = flattenHierarchy(hierarchyData, 0, null, null, timeEntriesByUser);

    // If user filter is applied, only show filtered users
    if (filterUser && filterUser.length > 0) {
      return allFlatUsers.filter(fu => filterUser.includes(fu.user.id));
    }

    // If group filter is applied, only show users who have entries
    // (backend already filters entries by group, so users without entries are not in the selected group)
    if (filterGroup && filterGroup.length > 0) {
      return allFlatUsers.filter(fu => {
        const userEntries = timeEntriesByUser.get(fu.user.id);
        return userEntries && userEntries.length > 0;
      });
    }

    return allFlatUsers;
  }, [hierarchyData, timeEntriesByUser, filterUser, filterGroup]);

  // Toggle team expansion (show/hide reports)
  // When collapsing, also collapse all descendants
  const handleToggleTeam = useCallback((userId: string) => {
    if (!hierarchyData) return;

    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        // Collapsing - remove this user and all descendants
        next.delete(userId);
        const node = findNodeById(hierarchyData, userId);
        if (node) {
          const descendantIds = collectDescendantIds(node);
          for (const id of descendantIds) {
            next.delete(id);
          }
        }
      } else {
        next.add(userId);
      }
      return next;
    });

    // Also collapse entries for descendants when collapsing team
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      if (expandedTeams.has(userId)) {
        // Was expanded, now collapsing - collapse entries for descendants too
        const node = findNodeById(hierarchyData, userId);
        if (node) {
          const descendantIds = collectDescendantIds(node);
          for (const id of descendantIds) {
            next.delete(id);
          }
        }
      }
      return next;
    });
  }, [hierarchyData, expandedTeams]);

  // Toggle entries expansion (show/hide time entries)
  const handleToggleEntries = (userId: string) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    if (hierarchyData) {
      const allIds = collectUserIds(hierarchyData);
      setExpandedTeams(new Set(allIds));
      setExpandedEntries(new Set(allIds));
    }
  };

  const handleCollapseAll = () => {
    setExpandedTeams(new Set());
    setExpandedEntries(new Set());
  };

  // Get all pending entries that can be selected
  // Admins can select their own entries, regular managers cannot
  const selectablePendingEntries = useMemo(() => {
    return timeEntries.filter(e => e.status === 'PENDING' && (hasAdminRole || e.userId !== currentUserId));
  }, [timeEntries, currentUserId, hasAdminRole]);

  // Selection handlers
  const handleSelectEntry = useCallback((entryId: string, selected: boolean) => {
    setSelectedEntryIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(entryId);
      } else {
        next.delete(entryId);
      }
      return next;
    });
  }, []);

  const handleSelectAllUser = useCallback((userId: string, selected: boolean) => {
    const userEntries = timeEntriesByUser.get(userId) || [];
    // Admins can select their own entries, regular managers cannot
    const pendingEntries = userEntries.filter(e => e.status === 'PENDING' && (hasAdminRole || e.userId !== currentUserId));

    setSelectedEntryIds(prev => {
      const next = new Set(prev);
      for (const entry of pendingEntries) {
        if (selected) {
          next.add(entry.id);
        } else {
          next.delete(entry.id);
        }
      }
      return next;
    });
  }, [timeEntriesByUser, currentUserId, hasAdminRole]);

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      setSelectedEntryIds(new Set(selectablePendingEntries.map(e => e.id)));
    } else {
      setSelectedEntryIds(new Set());
    }
  }, [selectablePendingEntries]);

  const handleClearSelection = useCallback(() => {
    setSelectedEntryIds(new Set());
  }, []);

  const renderInlineField = (
    entry: TimeEntry,
    field: 'workedDate' | 'customer' | 'engagement' | 'group' | 'type' | 'task' | 'hours' | 'description',
    display: React.ReactNode
  ) => {
    if (!onFieldUpdate) return display;
    return (
      <TimeEntryInlineEditableCell
        entry={entry}
        field={field}
        canEditTimeEntries={canModifyEntry(entry)}
        isAdminOrManager={isAdminOrManager}
        hasAdminRole={hasAdminRole}
        display={display}
        todayDate={todayDate}
        onUpdate={onFieldUpdate}
        isSaving={isSavingEntryId === entry.id}
        onValidationError={onValidationError}
      />
    );
  };

  // Get selected entries
  const selectedEntries = useMemo(() => {
    return timeEntries.filter(e => selectedEntryIds.has(e.id));
  }, [timeEntries, selectedEntryIds]);

  // Check if all selectable entries are selected
  const allSelected = selectablePendingEntries.length > 0 &&
    selectablePendingEntries.every(e => selectedEntryIds.has(e.id));
  const someSelected = selectedEntryIds.size > 0;

  // Render a time entry cell
  const renderTimeEntryCell = (entry: TimeEntry, columnKey: string) => {
    switch (columnKey) {
      case 'number':
        return canModifyEntry(entry) ? (
          <Link
            to={`/time-entries/${entry.id}/edit`}
            state={editListState}
            className="text-indigo-600 hover:text-indigo-900 hover:underline font-mono text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            {entry.number || '-'}
          </Link>
        ) : (
          <span className="font-mono text-xs text-gray-900">{entry.number || '-'}</span>
        );
      case 'workedDate':
        return renderInlineField(entry, 'workedDate', (formatWorkedDate ?? formatDate)(entry.workedDate));
      case 'user':
        return entry.User ? `${entry.User.firstName} ${entry.User.lastName}` : '-';
      case 'customer':
        return renderInlineField(entry, 'customer', entry.Customer?.accountName || '-');
      case 'engagement':
        return renderInlineField(entry, 'engagement', entry.Engagement?.name || '-');
      case 'group':
        return renderInlineField(entry, 'group', entry.Group?.name || '-');
      case 'type':
        return renderInlineField(
          entry,
          'type',
          <span title={typeLabels[entry.type] || entry.type} className="truncate max-w-[150px] block">
            {typeLabels[entry.type] || entry.type}
          </span>
        );
      case 'task':
        return renderInlineField(entry, 'task', entry.task || '-');
      case 'hours':
        return renderInlineField(entry, 'hours', <span className="font-medium">{formatTime(entry.timeSpent)}</span>);
      case 'description':
        return renderInlineField(
          entry,
          'description',
          <div className="max-w-xs truncate" title={stripHtml(entry.description || '')}>
            {stripHtml(entry.description || '') || '-'}
          </div>
        );
      case 'status':
        return <ApprovalStatusBadge status={entry.status} />;
      case 'createdAt':
        return <span className="text-gray-500">{formatDate(entry.createdAt)}</span>;
      default:
        return '-';
    }
  };

  if (isLoadingHierarchy) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-gray-500">Loading hierarchy...</span>
      </div>
    );
  }

  if (hierarchyError) {
    return (
      <div className="text-center py-12 text-red-600">
        Failed to load hierarchy. Please try again.
      </div>
    );
  }

  if (!hierarchyData) {
    return (
      <div className="text-center py-12 text-gray-500">
        <ClockIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
        <p>No hierarchy data available.</p>
      </div>
    );
  }

  const hasNoReports = !hierarchyData.children || hierarchyData.children.length === 0;

  // Check if the logged-in user has their own time entries
  const userOwnEntries = timeEntriesByUser.get(hierarchyData.id) || [];
  const hasOwnEntries = userOwnEntries.length > 0;

  // Only show "no reports" message if user has no reports AND no own entries
  if (hasNoReports && !hasOwnEntries) {
    return (
      <div className="text-center py-12 text-gray-500">
        <ClockIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
        <p>You don't have any direct reports to show in hierarchy view.</p>
        <p className="mt-2 text-sm">Your own time entries will appear here once you record them.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Bulk Action Bar - shown when entries are selected */}
      {someSelected && isAdminOrManager && (
        <div className="flex items-center justify-between px-4 py-3 bg-indigo-50 border-b border-indigo-200 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-indigo-900">
              {selectedEntryIds.size} {selectedEntryIds.size === 1 ? 'entry' : 'entries'} selected
            </span>
            <button
              onClick={handleClearSelection}
              className="text-xs text-indigo-600 hover:text-indigo-800 underline"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                onBulkApprove(selectedEntries);
                handleClearSelection();
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <CheckCircleIcon className="h-4 w-4" />
              Approve Selected
            </button>
            <button
              onClick={() => {
                onBulkReject(selectedEntries);
                handleClearSelection();
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <XCircleIcon className="h-4 w-4" />
              Reject Selected
            </button>
          </div>
        </div>
      )}

      <div className="overflow-auto flex-1" style={{ scrollbarGutter: 'stable' }}>
        {/* Toolbar - Expand/Collapse controls */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 bg-white rounded-md border border-gray-200 shadow-sm">
              <button
                onClick={handleExpandAll}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-l-md border-r border-gray-200 transition-colors"
              >
                Expand All
              </button>
              <button
                onClick={handleCollapseAll}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-r-md transition-colors"
              >
                Collapse All
              </button>
            </div>
            {isAdminOrManager && selectablePendingEntries.length > 0 && (
              <label className="inline-flex items-center gap-2 cursor-pointer bg-white px-3 py-1.5 rounded-md border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-xs font-medium text-gray-700">
                  Select all pending ({selectablePendingEntries.length})
                </span>
              </label>
            )}
          </div>
        </div>

        <table className="min-w-full">
          <thead className="bg-white border-b-2 border-gray-200 sticky top-0 z-10">
            <tr>
              {isAdminOrManager && (
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-10">
                  <span className="sr-only">Select</span>
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-72">
                Team Member
              </th>
            {visibleColumns.filter(col => col.key !== 'user').map((col) => (
              <th
                key={col.key}
                className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >
                {col.label}
              </th>
            ))}
            {(canEditTimeEntries || canCreateTimeEntries || isAdminOrManager) && (
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {flatUsers.map((flatUser, _index) => {
            const { user, level, isManager } = flatUser;
            const userEntries = timeEntriesByUser.get(user.id) || [];
            const isTeamExpanded = expandedTeams.has(user.id);
            const isEntriesExpanded = expandedEntries.has(user.id);
            const hasEntries = userEntries.length > 0;
            const isRoot = level === 0;

            // Check if this user's manager is collapsed (to hide this row)
            const parentManagerId = flatUser.managerId;
            if (parentManagerId && !expandedTeams.has(parentManagerId)) {
              return null; // Hide if parent manager's team is collapsed
            }

            // Calculate team stats for managers
            let teamEntries: TimeEntry[] = [];
            if (isManager) {
              const collectTeamEntries = (node: HierarchyNode): TimeEntry[] => {
                const entries: TimeEntry[] = [];
                entries.push(...(timeEntriesByUser.get(node.id) || []));
                for (const child of node.children || []) {
                  entries.push(...collectTeamEntries(child));
                }
                return entries;
              };
              teamEntries = collectTeamEntries(user);
            }

            const teamStatus = isManager ? calculateTeamApprovalStatus(teamEntries) : null;
            const statusStyle = teamStatus ? getApprovalStatusStyle(teamStatus) : null;

            // Calculate user's own stats
            const totalMinutes = userEntries.reduce((sum, e) => sum + (e.timeSpent || 0), 0);
            const totalHours = (totalMinutes / 60).toFixed(1);
            // Admins can select their own entries for approval, regular managers cannot
            const pendingEntries = userEntries.filter(e => e.status === 'PENDING' && (hasAdminRole || e.userId !== currentUserId));
            const pendingCount = userEntries.filter(e => e.status === 'PENDING').length;
            const approvedCount = userEntries.filter(e => e.status === 'APPROVED').length;
            const rejectedCount = userEntries.filter(e => e.status === 'REJECTED').length;

            // Check if all pending entries for this user are selected
            const userPendingSelected = pendingEntries.length > 0 &&
              pendingEntries.every(e => selectedEntryIds.has(e.id));
            const userSomePendingSelected = pendingEntries.some(e => selectedEntryIds.has(e.id));

            // Calculate level-based styling
            const levelColors = [
              'border-l-indigo-500 bg-indigo-50/50',  // Level 0 (You)
              'border-l-blue-400 bg-blue-50/30',      // Level 1
              'border-l-cyan-400 bg-cyan-50/20',      // Level 2
              'border-l-teal-400 bg-teal-50/10',      // Level 3+
            ];
            const levelColor = levelColors[Math.min(level, levelColors.length - 1)];

            return (
              <React.Fragment key={user.id}>
                {/* User Row */}
                <tr
                  className={`transition-colors border-l-4 ${levelColor} ${
                    isRoot ? 'hover:bg-indigo-100/50' :
                    isManager ? 'hover:bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Checkbox for selecting all user's pending entries */}
                  {isAdminOrManager && (
                    <td className="px-3 py-3 whitespace-nowrap text-center">
                      {pendingEntries.length > 0 && user.id !== currentUserId && (
                        <input
                          type="checkbox"
                          checked={userPendingSelected}
                          ref={el => {
                            if (el) el.indeterminate = userSomePendingSelected && !userPendingSelected;
                          }}
                          onChange={(e) => handleSelectAllUser(user.id, e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                          title={`Select all ${pendingEntries.length} pending entries for ${user.firstName}`}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center" style={{ paddingLeft: `${level * 24}px` }}>
                      {/* Team Expand/Collapse Button (only for managers) */}
                      {isManager ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleTeam(user.id);
                          }}
                          className={`w-6 h-6 flex items-center justify-center text-sm font-medium rounded-md mr-2 transition-all ${
                            isTeamExpanded
                              ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          title={isTeamExpanded ? 'Collapse team' : 'Expand team'}
                        >
                          {isTeamExpanded ? '−' : '+'}
                        </button>
                      ) : (
                        <span className="w-6 mr-2" />
                      )}

                      {/* Entries Expand/Collapse Button (only if has entries) */}
                      {hasEntries ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleEntries(user.id);
                          }}
                          className={`w-6 h-6 flex items-center justify-center rounded-md mr-3 transition-all ${
                            isEntriesExpanded
                              ? 'bg-gray-200 text-gray-700'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={isEntriesExpanded ? 'Hide entries' : 'Show entries'}
                        >
                          {isEntriesExpanded ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )}
                        </button>
                      ) : (
                        <span className="w-6 mr-3" />
                      )}

                      {/* User Icon */}
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                        isRoot ? 'bg-indigo-200 text-indigo-700' :
                        isManager ? 'bg-blue-100 text-blue-600' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {isManager ? (
                          <UserGroupIcon className="h-4 w-4" />
                        ) : (
                          <UserIcon className="h-4 w-4" />
                        )}
                      </div>

                      {/* User Name */}
                      <div className="flex flex-col">
                        <span className={`font-medium text-sm ${
                          isRoot ? 'text-indigo-900' :
                          isManager ? 'text-gray-900' :
                          'text-gray-700'
                        }`}>
                          {user.firstName} {user.lastName}
                          {isRoot && (
                            <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-medium">
                              You
                            </span>
                          )}
                        </span>
                        {user.jobTitle && (
                          <span className="text-xs text-gray-500">{user.jobTitle}</span>
                        )}
                      </div>

                      {/* Team Status Badge */}
                      {isManager && statusStyle && (
                        <span className={`ml-3 text-xs px-2 py-1 rounded-full font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                          {statusStyle.label}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Stats columns */}
                  {visibleColumns.filter(col => col.key !== 'user').map((col) => (
                    <td key={col.key} className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {col.key === 'hours' && hasEntries ? (
                        <span className="inline-flex items-center gap-1.5 bg-gray-100 px-2 py-1 rounded-md">
                          <ClockIcon className="h-3.5 w-3.5 text-gray-500" />
                          <span className="font-semibold text-gray-700">{totalHours}h</span>
                        </span>
                      ) : col.key === 'status' && hasEntries ? (
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-yellow-50 text-yellow-700 text-xs font-medium" title="Pending">
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
                            {pendingCount}
                          </span>
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-50 text-green-700 text-xs font-medium" title="Approved">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                            {approvedCount}
                          </span>
                          {rejectedCount > 0 && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-50 text-red-700 text-xs font-medium" title="Rejected">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                              {rejectedCount}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">{hasEntries ? '-' : 'No entries'}</span>
                      )}
                    </td>
                  ))}

                  {(canEditTimeEntries || canCreateTimeEntries || isAdminOrManager) && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-3">
                        {/* Show expand button when collapsed with pending entries */}
                        {isAdminOrManager && pendingCount > 0 && user.id !== currentUserId && !isEntriesExpanded && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleEntries(user.id);
                            }}
                            className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 text-xs font-semibold px-3 py-1.5 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                            title="Expand to approve/reject entries"
                          >
                            Review {pendingCount}
                          </button>
                        )}
                        <span className="text-xs text-gray-400 font-medium">
                          {hasEntries ? `${userEntries.length} ${userEntries.length === 1 ? 'entry' : 'entries'}` : '-'}
                        </span>
                      </div>
                    </td>
                  )}
                </tr>

                {/* Time Entry Rows (when entries are expanded) */}
                {isEntriesExpanded && userEntries.map((entry) => {
                  const isEntrySelected = selectedEntryIds.has(entry.id);
                  // Admins can select their own entries, regular managers cannot
                  const canSelectEntry = entry.status === 'PENDING' && (hasAdminRole || entry.userId !== currentUserId);

                  return (
                  <tr
                    key={entry.id}
                    className={`transition-colors border-l-4 border-l-transparent ${
                      isEntrySelected
                        ? 'bg-indigo-50/70 hover:bg-indigo-100/70'
                        : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    {/* Entry checkbox */}
                    {isAdminOrManager && (
                      <td className="px-3 py-2.5 whitespace-nowrap text-center">
                        {canSelectEntry && (
                          <input
                            type="checkbox"
                            checked={isEntrySelected}
                            onChange={(e) => handleSelectEntry(entry.id, e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2.5 whitespace-nowrap text-sm" style={{ paddingLeft: `${(level + 1) * 24 + 36}px` }}>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
                        {renderTimeEntryCell(entry, 'workedDate')}
                      </div>
                    </td>
                    {visibleColumns.filter(col => col.key !== 'user').map((col) => (
                      <td key={col.key} className="px-4 py-2.5 whitespace-nowrap text-sm">
                        {renderTimeEntryCell(entry, col.key)}
                      </td>
                    ))}
                    {(canEditTimeEntries || canCreateTimeEntries || isAdminOrManager) && (
                      <td className="px-4 py-2.5 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-1">
                          {canModifyEntry(entry) && (entry.status !== 'APPROVED' || hasAdminRole) && (
                            <Link
                              to={`/time-entries/${entry.id}/edit`}
                              state={editListState}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                              title="Edit"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </Link>
                          )}
                          {/* Admins can approve their own entries, regular managers cannot */}
                          {isAdminOrManager && entry.status === 'PENDING' && (hasAdminRole || entry.userId !== currentUserId) && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onApproveClick(entry);
                                }}
                                className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                                title="Approve"
                              >
                                <CheckCircleIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRejectClick(entry);
                                }}
                                className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                                title="Reject"
                              >
                                <XCircleIcon className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {canCopyEntry(entry) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onCopyClick!(entry);
                              }}
                              className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                              title="Copy"
                            >
                              <DocumentDuplicateIcon className="h-4 w-4" />
                            </button>
                          )}
                          {isAdminOrManager && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onActivityClick(entry);
                              }}
                              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                              title="View Activity"
                            >
                              <ClipboardDocumentListIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canDeleteEntry(entry) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteClick!(entry);
                              }}
                              className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}
